// The catnip trip on screen: a swirl and wave warp centred on the tripping cat, hue rotation,
// chromatic aberration and a feedback trail that smears colour into "colours that do not exist".
//
// The work happens in update(): the scene image (the effect pass's input) is warped, split and
// blended with last frame's trail into a ping-pong buffer. mainImage() then mixes that result in by
// `strength`, adding back what earlier effects in the pass (bloom) contributed, so glowing trip eyes
// still bloom. Strength 0 costs one texture read; the trail pass is skipped entirely.
import {
  HalfFloatType, LinearFilter, ShaderMaterial, Uniform, Vector2, WebGLRenderTarget, type Texture, type WebGLRenderer,
} from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import { BlendFunction, Effect } from 'postprocessing';

const TRAIL_FRAG = /* glsl */ `
uniform sampler2D tInput;
uniform sampler2D tPrev;
uniform float uStrength;
uniform float uTime;
uniform vec2 uCenter;
uniform float uAspect;
uniform float uTrail;
uniform float uKeep;
varying vec2 vUv;

vec3 hueRotate(vec3 c, float a) {
  const vec3 k = vec3(0.57735);
  float ca = cos(a);
  return c * ca + cross(k, c) * sin(a) + k * dot(k, c) * (1.0 - ca);
}

void main() {
  float s = uStrength;
  vec2 d = vUv - uCenter;
  d.x *= uAspect;
  float r = length(d);
  // swirl strongest near the cat, breathing in and out
  // the cat itself stays readable: the warp, split and trail fade out over a soft disc around it
  float keep = smoothstep(uKeep, uKeep * 2.0 + 0.04, r);
  // swirl strongest just outside the cat, breathing slowly in and out
  float swirl = s * 0.55 * keep * (1.0 - smoothstep(0.1, 1.0, r)) * sin(uTime * 0.45);
  float ca = cos(swirl), sa = sin(swirl);
  vec2 w = vec2(ca * d.x - sa * d.y, sa * d.x + ca * d.y);
  w.x /= uAspect;
  vec2 uv = uCenter + w;
  uv += s * keep * 0.0045 * vec2(sin(uv.y * 9.0 + uTime * 1.6), sin(uv.x * 7.0 - uTime * 1.3));
  // chromatic aberration along the direction from the cat
  vec2 dir = normalize(d + 1e-4) * vec2(1.0 / uAspect, 1.0) * (0.0015 + 0.004 * r) * s * keep;
  vec3 col = vec3(texture2D(tInput, uv + dir).r, texture2D(tInput, uv).g, texture2D(tInput, uv - dir).b);
  // colour drifts slowly in rings out from the cat instead of spinning the whole frame
  col = hueRotate(col, s * keep * (uTime * 0.35 + r * 1.6));
  // richer colour away from the cat, never blown out
  float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(col, mix(vec3(l), col, 1.35) * 1.08, s * keep);
  col = max(col, 0.0);
  // trail: last frame, zoomed slightly towards the cat and turned, fading
  vec2 pd = (vUv - uCenter) * (0.99 - 0.006 * s);
  float pa = 0.006 * s;
  vec2 puv = uCenter + vec2(cos(pa) * pd.x - sin(pa) * pd.y, sin(pa) * pd.x + cos(pa) * pd.y);
  vec3 prev = texture2D(tPrev, puv).rgb;
  prev = hueRotate(prev, 0.05 * s);
  vec3 outc = mix(col, max(col, prev * 0.9), uTrail * s * keep);
  gl_FragColor = vec4(outc, 1.0);
}`;

const COMPOSITE = /* glsl */ `
uniform sampler2D tTrip;
uniform sampler2D tRaw;
uniform float strength;
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  if (strength <= 0.0) { outputColor = inputColor; return; }
  vec3 earlier = max(inputColor.rgb - texture2D(tRaw, uv).rgb, 0.0); // what bloom added
  vec3 trip = texture2D(tTrip, uv).rgb + earlier;
  outputColor = vec4(mix(inputColor.rgb, trip, clamp(strength, 0.0, 1.0)), inputColor.a);
}`;

export class TripEffect extends Effect {
  /** 0..1, set from the trip beats each frame. */
  strength = 0;
  /** Screen position (0..1, origin bottom left) the swirl centres on. */
  readonly center = new Vector2(0.5, 0.5);
  /** Radius round `center`, in screen heights, the cat covers: warp, colour and trail leave it alone. */
  keepRadius = 0.08;
  private a: WebGLRenderTarget;
  private b: WebGLRenderTarget;
  private mat: ShaderMaterial;
  private quad: FullScreenQuad;
  private time = 0;
  private live = false;

  constructor() {
    super('CatnipTrip', COMPOSITE, {
      blendFunction: BlendFunction.SRC,
      uniforms: new Map<string, Uniform>([
        ['tTrip', new Uniform(null)],
        ['tRaw', new Uniform(null)],
        ['strength', new Uniform(0)],
      ]),
    });
    const opts = { type: HalfFloatType, minFilter: LinearFilter, magFilter: LinearFilter, depthBuffer: false };
    this.a = new WebGLRenderTarget(1, 1, opts);
    this.b = new WebGLRenderTarget(1, 1, opts);
    this.mat = new ShaderMaterial({
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: TRAIL_FRAG,
      uniforms: {
        tInput: { value: null }, tPrev: { value: null }, uStrength: { value: 0 }, uTime: { value: 0 },
        uCenter: { value: this.center }, uAspect: { value: 1 }, uTrail: { value: 0.5 }, uKeep: { value: 0.08 },
      },
      depthTest: false, depthWrite: false,
    });
    this.quad = new FullScreenQuad(this.mat);
  }

  override update(renderer: WebGLRenderer, inputBuffer: WebGLRenderTarget, deltaTime?: number): void {
    const s = Math.min(1, Math.max(0, this.strength));
    this.uniforms.get('strength')!.value = s;
    if (s <= 0.001) { this.live = false; return; }
    const dt = deltaTime ?? 1 / 60;
    this.time += dt;
    const w = inputBuffer.width, h = inputBuffer.height;
    if (this.a.width !== w || this.a.height !== h) { this.a.setSize(w, h); this.b.setSize(w, h); this.live = false; }
    const u = this.mat.uniforms;
    u.tInput!.value = inputBuffer.texture;
    // starting fresh: the trail starts from the current image, not from stale frames
    u.tPrev!.value = this.live ? this.a.texture : inputBuffer.texture;
    u.uStrength!.value = s;
    u.uTime!.value = this.time;
    u.uAspect!.value = w / Math.max(1, h);
    u.uKeep!.value = Math.min(0.4, Math.max(0.05, this.keepRadius));
    // the trail is a smear between neighbouring frames; at low frame rates it would become a
    // double exposure of images seconds apart, so it fades out as frames get further apart
    u.uTrail!.value = 0.5 * Math.min(1, Math.sqrt((1 / 60) / Math.max(dt, 1e-3)));
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this.b);
    this.quad.render(renderer);
    renderer.setRenderTarget(prev);
    [this.a, this.b] = [this.b, this.a];
    this.live = true;
    this.uniforms.get('tTrip')!.value = this.a.texture as Texture;
    this.uniforms.get('tRaw')!.value = inputBuffer.texture;
  }

  override setSize(width: number, height: number): void {
    this.a.setSize(width, height);
    this.b.setSize(width, height);
    this.live = false;
  }

  override dispose(): void {
    this.a.dispose(); this.b.dispose(); this.mat.dispose(); this.quad.dispose();
    super.dispose();
  }
}
